import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ShiftPaymentWithRelations } from "@/types/models";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DollarSignIcon, Terminal } from "lucide-react";
import { formatDateFull } from "@/lib/utils";

const formSchema = z.object({
  amount: z.coerce.number().min(0.01, "El monto debe ser mayor a 0"),
  reference: z.string().min(1, "La referencia es requerida"),
  rejection_reason: z.string().optional(),
});

type ReviewFormValues = z.infer<typeof formSchema>;

interface PaymentReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: ShiftPaymentWithRelations;
  onApprove: (payment: ShiftPaymentWithRelations, modifiedAmount?: number) => void;
  onReject: (payment: ShiftPaymentWithRelations, reason: string) => void;
}

const PaymentReviewModal = ({ isOpen, onClose, payment, onApprove, onReject }: PaymentReviewModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const { toast } = useToast();

  const form = useForm<ReviewFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: payment.amount,
      reference: payment.reference,
      rejection_reason: "",
    },
  });

  useEffect(() => {
    form.reset({
      amount: payment.amount,
      reference: payment.reference,
      rejection_reason: "",
    });
  }, [payment, form]);

  const handleApprove = () => {
    const { amount } = form.getValues();
    onApprove(payment, amount);
  };

  const handleReject = () => {
    const { rejection_reason } = form.getValues();
    if (!rejection_reason) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Debe proporcionar un motivo de rechazo.",
      });
      return;
    }
    onReject(payment, rejection_reason);
  };

  const handleClose = () => {
    setShowRejectionForm(false);
    onClose();
  }

  const watchedAmount = form.watch("amount");
  const exchangeRate = payment.exchange_rate;
  const amountInBs = watchedAmount && exchangeRate ? Math.ceil(watchedAmount * exchangeRate - 0.00001) : 0;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle>Revisar Pago</DialogTitle>
          <DialogHeader>
            <DialogDescription>
              Revise los detalles del pago y apruebe o rechace la transacción.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto pr-4">
              <div className="space-y-3">
                <Alert>
                    <Terminal className="h-4 w-4" />
                    <AlertTitle>Información de la Jornada</AlertTitle>
                    <AlertDescription>
                      <p><strong>Operador:</strong> {payment.shift.operator?.full_name}</p>
                      <p><strong>Sucursal:</strong> {payment.shift.operator.branch?.name}</p>
                      <p><strong>Fecha Inicio:</strong> {formatDateFull(payment.shift.start_time)}</p>
                      <p><strong>Fecha Fin:</strong> {formatDateFull(payment.shift.end_time)}</p>
                    </AlertDescription>
                </Alert>

                <Alert>
                    <DollarSignIcon className="h-4 w-4" />
                    <AlertTitle>Información pago</AlertTitle>
                    <AlertDescription>
                      <p><strong>Banco:</strong> {payment.bank?.name}</p>
                      <p><strong>Fecha Pago:</strong> {new Date(payment.created_at).toLocaleString()}</p>
                      <div className="w-full">
                        <p><strong>Comprobante de Pago:</strong></p>
                        <img
                          src={payment.receipt_photo_url}
                          alt="Comprobante"
                          className="rounded-lg border cursor-pointer w-32 h-32 object-cover"
                          onClick={() => setIsImageModalOpen(true)}
                        />
                      </div>
                      <div className="space-y-4">
                        <Form {...form}>
                          <form className="space-y-4">
                            <FormField
                              control={form.control}
                              name="amount"
                              render={({ field }) => (
                                <FormItem>
                                  <p className="mt-2"><strong>Monto (USD)</strong></p>
                                  {/* <FormLabel>Monto (USD)</FormLabel> */}
                                  <FormControl>
                                    <Input className="mt-0" type="number" step="0.01" {...field} />
                                  </FormControl>
                                  {watchedAmount > 0 && exchangeRate > 0 && (
                                    <p className="text-sm text-muted-foreground">
                                      <b>{amountInBs.toLocaleString('es-VE')} Bs.</b> (Tasa del pago: {exchangeRate.toFixed(2)} Bs.)
                                    </p>
                                  )}
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="reference"
                              render={({ field }) => (
                                <FormItem>
                                  <p className="mt-2"><strong>Referencia</strong></p>
                                  {/* <FormLabel>Referencia</FormLabel> */}
                                  <FormControl>
                                    <Input className="mt-0" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </form>
                        {showRejectionForm && (
                          <FormField
                            control={form.control}
                            name="rejection_reason"
                            render={({ field }) => (
                              <FormItem>
                                <p className="mt-2"><strong>Motivo del Rechazo</strong></p>
                                {/* <FormLabel>Motivo del Rechazo</FormLabel> */}
                                <FormControl>
                                  <Textarea placeholder="Explique por qué se rechaza el pago..." {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                        </Form>
                      </div>
                    </AlertDescription>
                </Alert>
              </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            {!showRejectionForm ? (
              <>
                <Button variant="outline" onClick={handleClose}>Cancelar</Button>
                <Button variant="destructive" onClick={() => setShowRejectionForm(true)}>Rechazar</Button>
                <Button onClick={handleApprove} disabled={isSubmitting}>Aprobar</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowRejectionForm(false)}>Atrás</Button>
                <Button variant="destructive" onClick={handleReject} disabled={isSubmitting}>Confirmar Rechazo</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
        <DialogContent className="max-w-4xl">
          <div className="max-h-[90vh] overflow-y-auto">
            <img src={payment.receipt_photo_url} alt="Comprobante" className="w-full h-auto" />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentReviewModal;